/**
 * 共有認証コア（frontend/shared/auth）。
 *
 * dev_auth(ローカル) / Cognito(本番) を統一インターフェースで扱う。
 * basePath などのアプリ差分は createAuth(config) で注入し、各アプリの lib/auth.ts は
 * createAuth(appConfig) の戻り値を名前付き再エクスポートする（呼び出し側の import は不変）。
 *
 * 本番では fetchAuthSession が期限切れトークンを自動リフレッシュする。
 * 401 等での明示リフレッシュは refreshAuthToken(forceRefresh) を使う。
 * リフレッシュ不能時は handleApiAuthError がタイムアウトのポップアップ→ログイン遷移を行う。
 */

import { logger } from './logger';
import type { AppAuthConfig } from './config';

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_TOKEN_EXPIRY_KEY = 'auth_token_expiry';

// Cognitoのサインアウトは失効済みセッション等で応答しない（ハングする）ことがあるため上限時間を設ける。
const COGNITO_SIGNOUT_TIMEOUT_MS = 1500;

/** 表示用の現在ログインユーザー情報。role はロール識別子（日本語ラベル化は表示側で行う）。 */
export interface CurrentUserInfo {
  name?: string;
  email?: string;
  role?: string;
}

/** createAuth が返す認証モジュール（api-core / hooks へ注入する）。 */
export interface AuthModule {
  // nextStep は Cognito(amplify) のサインインステップ。dev_auth では未設定。
  // ログイン画面が nextStep.signInStep（例: CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED）を参照する。
  // nextStep は Cognito(amplify) のサインインステップ（dev_auth では未設定）。
  // ログイン画面が `'nextStep' in result && result.nextStep?.signInStep` で参照するため、
  // 旧 amplify 型と同等の緩い型（any）にして呼び出し側の判定をそのまま通す。
  signIn: (username: string, password: string) => Promise<{ isSignedIn: boolean; nextStep?: any }>;
  confirmNewPassword: (password: string) => Promise<void>;
  requestPasswordReset: (username: string) => Promise<void>;
  confirmPasswordReset: (username: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearAuth: () => Promise<void>;
  isAuthenticated: () => Promise<boolean>;
  getAuthToken: () => Promise<string | null>;
  refreshAuthToken: () => Promise<string | null>;
  handleApiAuthError: () => Promise<void>;
  getCurrentUserInfo: () => Promise<CurrentUserInfo>;
}

const getDevAuthUrl = () => process.env.NEXT_PUBLIC_DEV_AUTH_URL || 'http://localhost:8090';

/**
 * アプリ設定を注入して認証モジュールを生成する。
 * セッションタイムアウトの多重発火ガードは createAuth のクロージャ単位（=アプリ単位）で保持する。
 */
export function createAuth(config: AppAuthConfig): AuthModule {
  const { basePath } = config;

  // ===== Token Storage（dev_auth 用） =====
  const saveToken = (token: string, expiresIn: number) => {
    if (typeof window === 'undefined') return;
    const expiryTime = Date.now() + expiresIn * 1000;
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(AUTH_TOKEN_EXPIRY_KEY, expiryTime.toString());
  };

  const getStoredToken = (): string | null => {
    if (typeof window === 'undefined') return null;
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const expiryStr = localStorage.getItem(AUTH_TOKEN_EXPIRY_KEY);
    if (!token || !expiryStr) return null;
    const expiry = parseInt(expiryStr, 10);
    if (Date.now() > expiry) {
      clearToken();
      return null;
    }
    return token;
  };

  const clearToken = () => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_TOKEN_EXPIRY_KEY);
  };

  // ===== Dev Auth (Local Development) =====
  interface DevAuthResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
  }

  const devAuthSignIn = async (username: string, password: string): Promise<boolean> => {
    try {
      clearToken();
      const response = await fetch(`${getDevAuthUrl()}/dev-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!response.ok) {
        logger.warn('dev_auth認証失敗');
        return false;
      }
      const data: DevAuthResponse = await response.json();
      saveToken(data.access_token, data.expires_in);
      logger.info('dev_auth認証成功');
      return true;
    } catch (error) {
      logger.error('dev_auth認証エラー', error);
      return false;
    }
  };

  const devAuthSignOut = async () => {
    clearToken();
    logger.info('dev_authサインアウト完了');
  };

  const devAuthIsAuthenticated = (): boolean => getStoredToken() !== null;

  // ===== Cognito Authentication (Production) =====
  const withTimeout = async (promise: Promise<unknown>, ms: number): Promise<void> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, ms);
    });
    try {
      await Promise.race([promise.catch(() => {}), timeout]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  const cognitoSignOut = async () => {
    await withTimeout(
      (async () => {
        try {
          const { signOut: amplifySignOut } = await import('aws-amplify/auth');
          await amplifySignOut();
        } catch (error) {
          logger.error('Cognitoサインアウトエラー', error);
        }
      })(),
      COGNITO_SIGNOUT_TIMEOUT_MS,
    );
  };

  const cognitoSignIn = async (username: string, password: string) => {
    try {
      const { signIn: amplifySignIn } = await import('aws-amplify/auth');
      const { isSignedIn, nextStep } = await amplifySignIn({ username, password });
      return { isSignedIn, nextStep };
    } catch (error: any) {
      if (error.name === 'UserAlreadyAuthenticatedException' || error.message === 'There is already a signed in user.') {
        logger.info('既存セッションをクリアして再ログイン');
        await cognitoSignOut();
        const { signIn: amplifySignIn } = await import('aws-amplify/auth');
        const { isSignedIn, nextStep } = await amplifySignIn({ username, password });
        return { isSignedIn, nextStep };
      }
      logger.error('Cognito認証エラー', error);
      throw error;
    }
  };

  const cognitoIsAuthenticated = async (): Promise<boolean> => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      return session.tokens !== undefined;
    } catch {
      return false;
    }
  };

  const cognitoGetToken = async (): Promise<string | null> => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  };

  // ===== User Info (表示用) =====
  const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    try {
      const part = token.split('.')[1];
      if (!part) return null;
      const padded = part.replace(/-/g, '+').replace(/_/g, '/');
      const base64 = padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '=');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join(''),
      );
      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return null;
    }
  };

  const asString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length > 0 ? value : undefined;

  const getCurrentUserInfo = async (): Promise<CurrentUserInfo> => {
    // この関数はいかなる経路でも throw してはならない（呼び出し側が throw を「認証失敗」と解釈するため）。
    try {
      if (process.env.NEXT_PUBLIC_AUTH_MODE === 'local') {
        const token = getStoredToken();
        if (!token) return {};
        const claims = decodeJwtPayload(token);
        if (!claims) return {};
        return { name: asString(claims.sub), role: asString(claims.role) };
      }

      try {
        const { fetchUserAttributes } = await import('aws-amplify/auth');
        const attrs = await fetchUserAttributes();
        const name = asString(attrs.name) ?? asString(attrs.preferred_username);
        const email = asString(attrs.email);
        if (name || email) {
          return { name, email };
        }
      } catch (error) {
        logger.warn('fetchUserAttributes に失敗。idToken のデコードにフォールバックします', error);
      }

      const token = await cognitoGetToken();
      if (token) {
        const claims = decodeJwtPayload(token);
        if (claims) {
          const name =
            asString(claims.name) ??
            asString(claims['cognito:username']) ??
            asString(claims.preferred_username);
          return {
            name,
            email: asString(claims.email),
            role: asString(claims['custom:role']) ?? asString(claims.role),
          };
        }
      }
      return {};
    } catch (error) {
      logger.warn('getCurrentUserInfo: ユーザー情報の取得に失敗しました', error);
      return {};
    }
  };

  // ===== Unified Interface =====
  const signIn = async (username: string, password: string) => {
    if (process.env.NEXT_PUBLIC_AUTH_MODE === 'local') {
      const isSignedIn = await devAuthSignIn(username, password);
      return { isSignedIn };
    }
    return await cognitoSignIn(username, password);
  };

  const confirmNewPassword = async (password: string) => {
    if (process.env.NEXT_PUBLIC_AUTH_MODE === 'local') return;
    try {
      const { confirmSignIn } = await import('aws-amplify/auth');
      await confirmSignIn({ challengeResponse: password });
    } catch (error) {
      logger.error('パスワード確認エラー', error);
      throw error;
    }
  };

  const requestPasswordReset = async (username: string) => {
    if (process.env.NEXT_PUBLIC_AUTH_MODE === 'local') return;
    try {
      const { resetPassword } = await import('aws-amplify/auth');
      await resetPassword({ username });
    } catch (error) {
      logger.error('パスワードリセットリクエストエラー', error);
      throw error;
    }
  };

  const confirmPasswordReset = async (username: string, code: string, newPassword: string) => {
    if (process.env.NEXT_PUBLIC_AUTH_MODE === 'local') return;
    try {
      const { confirmResetPassword } = await import('aws-amplify/auth');
      await confirmResetPassword({ username, confirmationCode: code, newPassword });
    } catch (error) {
      logger.error('パスワードリセット確認エラー', error);
      throw error;
    }
  };

  const signOut = async () => {
    if (process.env.NEXT_PUBLIC_AUTH_MODE === 'local') {
      await devAuthSignOut();
      window.location.href = `${basePath}/login`;
      return;
    }
    await cognitoSignOut();
    window.location.href = `${basePath}/login`;
  };

  const clearAuth = async () => {
    if (process.env.NEXT_PUBLIC_AUTH_MODE === 'local') {
      await devAuthSignOut();
      return;
    }
    await cognitoSignOut();
  };

  const isAuthenticated = async (): Promise<boolean> => {
    if (process.env.NEXT_PUBLIC_AUTH_MODE === 'local') {
      if (typeof window === 'undefined') return false;
      return devAuthIsAuthenticated();
    }
    return await cognitoIsAuthenticated();
  };

  const getAuthToken = async (): Promise<string | null> => {
    if (process.env.NEXT_PUBLIC_AUTH_MODE === 'local') {
      return getStoredToken();
    }
    return await cognitoGetToken();
  };

  const refreshAuthToken = async (): Promise<string | null> => {
    if (process.env.NEXT_PUBLIC_AUTH_MODE === 'local') {
      return null;
    }
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession({ forceRefresh: true });
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      logger.error('トークンのリフレッシュに失敗しました', error);
      return null;
    }
  };

  // セッションタイムアウトの多重発火ガード（アプリ単位・クロージャ保持）。
  let sessionTimeoutHandling = false;

  const handleApiAuthError = async () => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname.includes('/login')) return;
    if (sessionTimeoutHandling) return;
    sessionTimeoutHandling = true;

    window.alert('ログインがタイムアウトしました。ログインしなおしてください');
    void clearAuth();
    window.location.href = `${basePath}/login`;
  };

  return {
    signIn,
    confirmNewPassword,
    requestPasswordReset,
    confirmPasswordReset,
    signOut,
    clearAuth,
    isAuthenticated,
    getAuthToken,
    refreshAuthToken,
    handleApiAuthError,
    getCurrentUserInfo,
  };
}
